/**
 * API Fetch Utility
 * Wrapper around fetch() that automatically handles CSRF tokens
 * Use this instead of direct fetch() calls for API requests
 */

import { API_BASE_URL } from './apiConfig';
import { getCsrfToken, needsCsrfToken, clearCsrfToken } from './csrfToken';

/**
 * Enhanced fetch with automatic CSRF token handling
 * @param {string} url - API endpoint (relative or absolute)
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, options = {}) {
  // Ensure URL is absolute
  const absoluteUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  // Ensure credentials are included
  const fetchOptions = {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.headers || {}),
    },
  };

  // Add CSRF token for state-changing operations
  if (needsCsrfToken(options.method)) {
    try {
      const csrfToken = await getCsrfToken();
      if (csrfToken) {
        fetchOptions.headers['x-csrf-token'] = csrfToken;
        console.debug(`CSRF token added to ${options.method} request to ${url}`);
      } else {
        console.error('CSRF token is null/undefined - request will fail with 403');
        // Try to fetch a fresh token as a last resort
        try {
          clearCsrfToken();
          const freshToken = await getCsrfToken();
          if (freshToken) {
            fetchOptions.headers['x-csrf-token'] = freshToken;
            console.debug('CSRF token retrieved after retry');
          } else {
            console.error('Failed to retrieve CSRF token after retry');
          }
        } catch (retryError) {
          console.error('Failed to retry CSRF token fetch:', retryError);
        }
      }
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
      // Try one more time with a fresh fetch
      try {
        clearCsrfToken();
        const retryToken = await getCsrfToken();
        if (retryToken) {
          fetchOptions.headers['x-csrf-token'] = retryToken;
          console.debug('CSRF token retrieved after error retry');
        } else {
          console.error('CSRF token still unavailable after retry - request will likely fail');
        }
      } catch (retryError) {
        console.error('CSRF token retry also failed:', retryError);
      }
    }
  }

  // Make the request
  let response = await fetch(absoluteUrl, fetchOptions);

  // Handle CSRF token errors (403)
  if (response.status === 403) {
    try {
      const errorData = await response.json();
      if (errorData?.error?.code === 'CSRF_TOKEN_MISSING' || errorData?.error?.code === 'CSRF_TOKEN_INVALID') {
        // Clear cached token and retry once
        clearCsrfToken();
        const csrfToken = await getCsrfToken();
        
        if (csrfToken) {
          fetchOptions.headers['x-csrf-token'] = csrfToken;
          response = await fetch(absoluteUrl, fetchOptions);
        }
      }
    } catch (parseError) {
      // If we can't parse the error, just return the original response
      console.warn('Failed to parse CSRF error:', parseError);
    }
  }

  return response;
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: (url, options = {}) => apiFetch(url, { ...options, method: 'GET' }),
  post: (url, data, options = {}) => apiFetch(url, {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: JSON.stringify(data),
  }),
  put: (url, data, options = {}) => apiFetch(url, {
    ...options,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: JSON.stringify(data),
  }),
  patch: (url, data, options = {}) => apiFetch(url, {
    ...options,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    body: JSON.stringify(data),
  }),
  delete: (url, options = {}) => apiFetch(url, { ...options, method: 'DELETE' }),
};

